import { byIso2 } from "@shopexperts/shared";
import type { ProjectStatus, ProjectType } from "@/v2Projects/types";

interface StatusConfig {
  label: string;
  badgeClass: string;
  accentBorder: string;
  avatarRing: string;
}

export function getStatusConfig(status: ProjectStatus, projectType?: ProjectType | null): StatusConfig {
  switch (status) {
    case "pending":
      if (projectType === "booking_call") {
        return {
          label: "Call Request",
          badgeClass: "bg-sky-50 text-sky-700 border border-sky-200",
          accentBorder: "border-l-sky-400",
          avatarRing: "ring-sky-400",
        };
      }

      return {
        label: "Pending Quote",
        badgeClass: "bg-amber-50 text-amber-700 border border-amber-200",
        accentBorder: "border-l-amber-400",
        avatarRing: "ring-amber-400",
      };
    case "quoted":
      return {
        label: "Quote Submitted",
        badgeClass: "bg-violet-50 text-violet-700 border border-violet-200",
        accentBorder: "border-l-violet-500",
        avatarRing: "ring-violet-500",
      };
    case "in_progress":
      return {
        label: "In Progress",
        badgeClass: "bg-blue-50 text-blue-700 border border-blue-200",
        accentBorder: "border-l-blue-500",
        avatarRing: "ring-blue-500",
      };
    case "submitted":
      return {
        label: "Submitted",
        badgeClass: "bg-indigo-50 text-indigo-700 border border-indigo-200",
        accentBorder: "border-l-indigo-500",
        avatarRing: "ring-indigo-500",
      };
    case "completed":
      return {
        label: "Completed",
        badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200",
        accentBorder: "border-l-emerald-500",
        avatarRing: "ring-emerald-500",
      };
    case "cancelled":
      return {
        label: "Cancelled",
        badgeClass: "bg-gray-100 text-gray-600 border border-gray-200",
        accentBorder: "border-l-gray-400",
        avatarRing: "ring-gray-300",
      };
    case "rejected":
      return {
        label: "Rejected",
        badgeClass: "bg-red-50 text-red-700 border border-red-200",
        accentBorder: "border-l-red-400",
        avatarRing: "ring-red-400",
      };
    default:
      return {
        label: status,
        badgeClass: "bg-gray-100 text-gray-600 border border-gray-200",
        accentBorder: "border-l-gray-400",
        avatarRing: "ring-gray-300",
      };
  }
}

export function formatLocalTime(timezone: string | null | undefined): string | null {
  if (!timezone) return null;

  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    })
      .format(new Date())
      .replace(" AM", "am")
      .replace(" PM", "pm");
  } catch {
    return null;
  }
}

const PARTNER_TIER_LABELS: Record<string, string> = {
  shopify_plus: "Shopify Plus",
  shopify: "Shopify Partner",
};

export function getPartnerTierLabel(tier: string | null | undefined): string | null {
  if (!tier) return null;
  return PARTNER_TIER_LABELS[tier] ?? null;
}

export function formatPrice(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(dollars % 1000 === 0 ? 0 : 1)}k`;
  return `$${dollars.toFixed(0)}`;
}

function normalizeCurrency(currency: string | null | undefined): string {
  const normalized = (currency || "USD").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : "USD";
}

export function formatQuoteAmount(
  amountCents: number,
  interval: string,
  quantity: number,
  currency?: string | null,
): string {
  const amount = formatCurrency(amountCents, currency, { maximumFractionDigits: 0 });
  if (interval === "hourly") return `${amount}/hr × ${quantity}h`;
  return amount;
}

export function formatCurrency(
  cents: number,
  currency?: string | null,
  options?: { maximumFractionDigits?: number },
): string {
  const safeCurrency = normalizeCurrency(currency);
  const maximumFractionDigits = options?.maximumFractionDigits ?? 2;
  const minimumFractionDigits = Math.min(cents % 100 === 0 ? 0 : 2, maximumFractionDigits);

  try {
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: safeCurrency,
      minimumFractionDigits,
      maximumFractionDigits,
    });
  } catch {
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits,
      maximumFractionDigits,
    });
  }
}

export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60_000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;

  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatCompactDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function calculateDeliveryDeadline(
  startDate: Date,
  deliveryAfterDays: number,
  includesWeekends: boolean,
): Date {
  const date = new Date(startDate);
  date.setHours(0, 0, 0, 0);

  if (deliveryAfterDays <= 0) {
    return date;
  }

  let remainingDays = deliveryAfterDays;

  while (remainingDays > 0) {
    date.setDate(date.getDate() + 1);

    if (includesWeekends) {
      remainingDays -= 1;
      continue;
    }

    const day = date.getDay();
    const isWeekend = day === 0 || day === 6;
    if (!isWeekend) {
      remainingDays -= 1;
    }
  }

  return date;
}

export function getDeliveryWindowLabel(
  deliveryAfterDays: number,
  includesWeekends: boolean,
): string {
  if (includesWeekends) {
    return `${deliveryAfterDays} day${deliveryAfterDays === 1 ? "" : "s"}, including weekends`;
  }
  return `${deliveryAfterDays} work day${deliveryAfterDays === 1 ? "" : "s"}`;
}

export function findSelectedProject<
  T extends { project_status: ProjectStatus },
>(projects: T[]): T | null {
  const priority: ProjectStatus[] = [
    "in_progress",
    "submitted",
    "quoted",
    "completed",
  ];

  for (const status of priority) {
    const found = projects.find((p) => p.project_status === status);
    if (found) return found;
  }

  return null;
}

export function getEarliestDate(dates: (string | null | undefined)[]): string | null {
  const valid = dates.filter((d): d is string => !!d);
  if (valid.length === 0) return null;
  const sorted = valid.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  return sorted[0] ?? null;
}

export function resolveLocationDisplay(countryAndCity: string): string {
  const trimmed = countryAndCity.trim();

  if (/^[A-Z]{2}$/.test(trimmed)) {
    return byIso2[trimmed]?.name ?? trimmed;
  }

  const parts = trimmed.split(",").map((s) => s.trim());
  if (parts.length >= 2) {
    const last: string | undefined = parts[parts.length - 1];
    if (last && /^[A-Z]{2}$/.test(last)) {
      parts[parts.length - 1] = byIso2[last]?.name ?? last;
    }
    return parts.join(", ");
  }

  return trimmed;
}
